import api from '@/services/apiService';

export interface ResponseArenaDetail {
  success: boolean;
  message: string;
  data: DataArenaDetail;
}

export interface DataArenaDetail {
  slug: string;
  name: string;
  description: string;
  address: any;
  kode_wilayah: string;
  currency: string;
  sport_categories: SportCategory[];
  facilities: Facilities;
  images: ArenaImages;
  maps: Maps;
  opening_hours: OpeningHours;
  social_links: SocialLinks;
  rules: string[];
  is_refundable: boolean;
  physical_fields: PhysicalField[];
}

export interface SportCategory {
  slug: string;
  name: string;
  icon: string;
}

export interface Facilities {
  data: Facility[];
}

export interface Facility {
  name: string;
  slug: string;
  icon: string;
  color: string;
  description?: string;
}

export interface ArenaImages {}

export interface Maps {
  google_maps_url?: string;
  city?: string;
  address?: string;
  kecamatan?: string;
}

export interface OpeningHours {
  friday: string;
  monday: string;
  saturday: string;
  sunday: string;
  thursday: string;
  tuesday: string;
  wednesday: string;
}

export interface SocialLinks {
  facebook: string;
  instagram: string;
  website: string;
}

export interface PhysicalField {
  slug: string;
  name: string;
  description: string;
  capacity: number;
  size: string;
  surface_type: string;
  images: PhysicalImages;
  location_info: LocationInfo;
  fields: Field[];
}

export interface PhysicalImages {}

export interface LocationInfo {}

export interface Field {
  slug: string;
  name: string;
  description: string;
  capacity: number;
  images: FieldImages;
  type: string;
  field_category: FieldCategory;
  field_time_slot?: FieldTimeSlot[];
}

export interface FieldImages {
  additionalProp1?: AdditionalProp1;
}

export interface AdditionalProp1 {}

export interface FieldCategory {
  slug: string;
  name: string;
  icon?: string;
}

export interface FieldTimeSlot {
  public_id: string;
  day_of_week: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  base_price: number;
  final_price: number;
  is_available: boolean;
  can_be_used: boolean;
}

export const getArenaDetail = async (slug: string, date?: string): Promise<ResponseArenaDetail> => {
  const q = new URLSearchParams();
  if (date) q.set('date', date);
  const queryString = date ? `?${q.toString()}` : '';

  const { data } = await api.get(`/arenas/detail/${slug}${queryString}`);
  return data;
};

export interface FieldListPhysicalField {
  id: number;
  name: string;
  slug: string;
  variant_count: number;
  is_shared: boolean;
}

export interface FieldListItem {
  id: number;
  name: string;
  price: number;
  max_price?: number;
  category: string;
  status: string | boolean;
  is_active: boolean;
  images: string[];
  slug: string;
  title?: string;
  type?: string;
  field_type?: string;
  surface_type?: string;
  size?: string;
  open_time?: string;
  close_time?: string;
  slot_duration_minutes?: number;
  image?: string;
  physical_field?: FieldListPhysicalField;
  config?: any;
}

export interface FieldsListResponse {
  success: boolean;
  message: string;
  data: {
    fields: FieldListItem[];
    pagination: {
      current_page: number;
      has_next: boolean;
      has_prev: boolean;
      items_per_page: number;
      total_items: number;
      total_pages: number;
    };
  };
}


export const getFieldsForArena = async (
  arenaSlug: string,
  page = 1,
  limit = 12,
  search?: string,
  sortBy?: string,
  order?: 'asc' | 'desc'
): Promise<FieldsListResponse> => {
  const q = new URLSearchParams();
  q.set('page', String(page));
  q.set('limit', String(limit));
  if (search) q.set('search', search);
  if (sortBy) q.set('sort_by', sortBy);
  if (order) q.set('order', order);

  const { data } = await api.get(`/arenas/${arenaSlug}/fields?${q.toString()}`);
  return data as FieldsListResponse;
};

export interface FieldAvailabilitySlot {
  id: string;
  start: string; // ISO datetime
  end: string; // ISO datetime
  title?: string;
  price?: number | string;
  is_available?: boolean;
  // Optional properties for FullCalendar events (if API returns them directly)
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
  extendedProps?: {
    price?: string;
    [key: string]: any;
  };
}

export interface FieldAvailabilityResponse extends Array<FieldAvailabilitySlot> {
  // API returns direct array of FieldAvailabilitySlot
}

export const getFieldAvailability = async (
  arenaSlug: string,
  fieldSlug: string
): Promise<FieldAvailabilityResponse> => {
  const { data } = await api.get(`/arena/${arenaSlug}/availability/${fieldSlug}`);
  return data as FieldAvailabilityResponse;
};

export interface FieldWithAvailability {
  field_slug: string;
  field_name: string;
  color: string;
  events: FieldAvailabilitySlot[];
}

export interface AllFieldsAvailabilityResponse {
  success: boolean;
  message: string;
  data: {
    fields: FieldWithAvailability[];
  };
}

export const getAllFieldsAvailability = async (
  arenaSlug: string
): Promise<AllFieldsAvailabilityResponse> => {
  const { data } = await api.get(`/arena/${arenaSlug}/availability`);
  return data as AllFieldsAvailabilityResponse;
};

export interface FieldDetailResponse {
  success: boolean;
  message: string;
  data: {
    field: {
      id: number;
      slug: string;
      name: string;
      description?: string | null;
      type?: string;
      arena?: any;
      physical_field?: any;
      sport_category?: {
        id: number;
        name: string;
      };
      [key: string]: any;
    };
  };
}

export const getFieldDetail = async (
  arenaSlug: string,
  fieldSlug: string
): Promise<FieldDetailResponse> => {
  const { data } = await api.get(`/arena/${arenaSlug}/fields/${fieldSlug}`);
  return data as FieldDetailResponse;
};

export interface TimeSlot {
  id: number;
  public_id?: string;
  start_time: string; // HH:MM:SS
  end_time: string; // HH:MM:SS
  is_available: boolean;
  final_price?: number;
  slot_order?: number;
  [key: string]: any;
}

export interface TimeSlotsResponse {
  success: boolean;
  message: string;
  data: {
    time_slots: TimeSlot[];
  };
}

export const getFieldTimeSlots = async (
  arenaSlug: string,
  fieldSlug: string,
  date: string
): Promise<TimeSlotsResponse> => {
  const q = new URLSearchParams();
  q.set('date', date);
  const { data } = await api.get(
    `/arena/${arenaSlug}/fields/${fieldSlug}/time-slots?${q.toString()}`
  );
  return data as TimeSlotsResponse;
};
